import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ReactPaginate from 'react-paginate';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import { useEducation } from '../../context/educationContext';
import './educacao.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Drawer from '../../components/Drawer';

const PainelSeduc = () => {
  const {
    listClasses,
    listSubjects,
    listEnrollments,
    listCourses,
    listSchoolUnits,
    listTeachers,
  } = useEducation();

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [schoolUnits, setSchoolUnits] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [pageByCard, setPageByCard] = useState({
    subjects: 1,
    courses: 1,
    schools: 1,
    teachers: 1,
    classes: 1,
    enrollments: 1,
  });
  const [countByCard, setCountByCard] = useState({});

  const [filter, setFilter] = useState('');
  const [drawer, setDrawer] = useState({ open: false, rows: [], title: '' });
  const [activeCard, setActiveCard] = useState('subjects');

  const placeholders = {
    subjects: 'Buscar por turma, curso ou professor',
    courses: 'Buscar por curso, módulo ou dias',
    schools: 'Buscar por unidade, cidade ou regional',
    teachers: 'Buscar por professor ou documento',
    classes: 'Buscar por turma, curso ou unidade',
    enrollments: 'Buscar por aluno ou turma',
  };

  const cards = useMemo(() => ([
    { key: 'subjects', label: 'Disciplinas ofertadas', count: (countByCard.subjects ?? subjects?.length) || 0, color: 'card-blue' },
    { key: 'courses', label: 'Cursos', count: (countByCard.courses ?? courses?.length) || 0, color: 'card-green' },
    { key: 'schools', label: 'Unidades escolares', count: (countByCard.schools ?? schoolUnits?.length) || 0, color: 'card-purple' },
    { key: 'teachers', label: 'Professores', count: (countByCard.teachers ?? teachers?.length) || 0, color: 'card-orange' },
    { key: 'classes', label: 'Turmas', count: (countByCard.classes ?? classesData?.length) || 0, color: 'card-cyan' },
    { key: 'enrollments', label: 'Matrículas', count: (countByCard.enrollments ?? enrollments?.length) || 0, color: 'card-pink' },
  ]), [countByCard, subjects, courses, schoolUnits, teachers, classesData, enrollments]);

  const displayedData = useMemo(() => {
    const term = filter.toLowerCase();
    const match = (text) => String(text || '').toLowerCase().includes(term);
    switch (activeCard) {
      case 'courses':
        return courses?.filter((c) => !term || match(c.name) || match(c.modulo) || match(c.days)) || [];
      case 'schools':
        return schoolUnits?.filter((s) => !term || match(s.name) || match(s.city_name) || match(s.nome_gre)) || [];
      case 'teachers':
        return teachers?.filter((t) => !term || match(t.name) || match(t.social_name) || match(t.cpf)) || [];
      case 'classes':
        return classesData?.filter((c) => !term || match(c.name) || match(c.course_name) || match(c.unit_name)) || [];
      case 'enrollments':
        return enrollments?.filter((e) => !term || match(e.internal) || match(e.class_education)) || [];
      case 'subjects':
      default:
        return subjects?.filter((s) =>
          !term ||
          match(s.type_name) ||
          match(s.education_class) ||
          match(s.course) ||
          match(s.teacher_name)
        ) || [];
    }
  }, [activeCard, filter, subjects, courses, schoolUnits, teachers, classesData, enrollments]);

  const handleOpenDrawer = (item, title) => {
    const entries = buildDrawerEntries(item).map((row) => ({
      ...row,
      value: formatValue(row.value),
    }));
    setDrawer({ open: true, rows: entries, title });
  };
  const handleCloseDrawer = () => setDrawer({ open: false, rows: [], title: '' });

  const dataSources = useMemo(() => ({
    subjects: { fn: listSubjects, setter: setSubjects },
    courses: { fn: listCourses, setter: setCourses },
    schools: { fn: listSchoolUnits, setter: setSchoolUnits },
    teachers: { fn: listTeachers, setter: setTeachers },
    classes: { fn: listClasses, setter: setClassesData },
    enrollments: { fn: listEnrollments, setter: setEnrollments },
  }), [listSubjects, listCourses, listSchoolUnits, listTeachers, listClasses, listEnrollments]);

  const fetchCard = useCallback(async (key, page = 1, size = pageSize) => {
    const source = dataSources[key];
    if (!source?.fn) return;
    try {
      const offset = (page - 1) * size;
      const res = await source.fn({ page, page_size: size, limit: size, offset });
      const results = res?.results || res || [];
      source.setter(results);
      const count = typeof res?.count === 'number' ? res.count : results.length;
      setCountByCard((prev) => ({ ...prev, [key]: count }));
    } catch (err) {
      console.error(`Erro ao carregar ${key}:`, err);
    }
  }, [dataSources, pageSize]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all(Object.keys(dataSources).map((key) => fetchCard(key, 1, pageSize)));
      setLoading(false);
    };

    loadAll();
  }, [dataSources, fetchCard, pageSize]);

  useEffect(() => {
    const page = pageByCard[activeCard] || 1;
    fetchCard(activeCard, page, pageSize);
  }, [activeCard, pageByCard, pageSize, fetchCard]);

  const handleItemsPerPageChange = (e) => {
    const nextSize = Number(e.target.value);
    setPageSize(nextSize);
    setPageByCard((prev) => ({ ...prev, [activeCard]: 1 }));
  };

  const handlePageClick = (data) => {
    const nextPage = data.selected + 1;
    setPageByCard((prev) => ({ ...prev, [activeCard]: nextPage }));
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d) ? value : d.toLocaleDateString('pt-BR');
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    return String(value);
  };

  const buildDrawerEntries = (item) => {
    switch (activeCard) {
      case 'subjects':
        return [
          { label: 'Disciplina', value: item.type_name },
          { label: 'Turma', value: item.education_class },
          { label: 'Curso', value: item.course },
          { label: 'Professor', value: item.teacher_name },
          { label: 'Carga horária', value: item.workload },
          { label: 'Horas semanais', value: item.qtdHSemanais },
          { label: 'Data', value: formatDate(item.date) },
        ];
      case 'courses':
        return [
          { label: 'Curso', value: item.name },
          { label: 'Módulo', value: item.modulo },
          { label: 'Dias', value: item.days },
          { label: 'Duração', value: item.duration },
          { label: 'EAD', value: formatValue(item.ead) },
          { label: 'Individual', value: formatValue(item.individual) },
          { label: 'Início', value: formatDate(item.start) },
          { label: 'Fim', value: formatDate(item.end) },
        ];
      case 'schools':
        return [
          { label: 'Unidade', value: item.name },
          { label: 'Código', value: item.unit_code },
          { label: 'Cidade', value: item.city_name },
          { label: 'Estado', value: item.state },
          { label: 'Nome GRE', value: item.nome_gre },
          { label: 'Telefone', value: item.phone },
          { label: 'E-mail', value: item.email },
          { label: 'Bairro', value: item.neighborhood },
          { label: 'Logradouro', value: item.public_place },
          { label: 'CEP', value: item.cep },
        ];
      case 'teachers':
        return [
          { label: 'Nome', value: item.name },
          { label: 'Nome social', value: item.social_name },
          { label: 'CPF', value: item.cpf },
          { label: 'Matrícula', value: item.registration },
          { label: 'Nascimento', value: formatDate(item.date_of_birth) },
          { label: 'Nacionalidade', value: item.nationality },
          { label: 'Naturalidade', value: item.naturalness },
        ];
      case 'classes':
        return [
          { label: 'Turma', value: item.name },
          { label: 'Código', value: item.class_code },
          { label: 'Curso', value: item.course_name || item.course },
          { label: 'Unidade escolar', value: item.school_name || item.unit_name },
          { label: 'Turno', value: item.shift },
          { label: 'Dias', value: item.days },
          { label: 'Duração', value: item.duration },
          { label: 'Módulo', value: item.modulo },
          { label: 'Data', value: formatDate(item.date) },
          { label: 'Localização', value: item.location },
          { label: 'Ativa', value: item.actvite },
        ];
      case 'enrollments':
        return [
          { label: 'Aluno', value: item.internal_name },
          { label: 'Turma', value: item.class_education_name },
          { label: 'Curso', value: item.class_education_course?.name },
          { label: 'Unidade escolar', value: item.class_education_schoolunit?.name },
          { label: 'Início', value: formatDate(item.start) },
          { label: 'Fim', value: formatDate(item.end) },
          { label: 'Status ativo', value: item.class_education_actvite },
          { label: 'Localização', value: item.class_education_location },
        ];
      default:
        return Object.entries(item || {})
          .filter(([k]) => !k.toLowerCase().startsWith('id'))
          .map(([k, v]) => ({ label: k, value: v }));
    }
  };

  const renderSubtitle = (item) => {
    switch (activeCard) {
      case 'subjects':
        return `Turma: ${item.education_class || '—'} • Curso: ${item.course || '—'} • Professor: ${item.teacher_name || '—'}`;
      case 'courses':
        return `Módulo: ${item.modulo || '—'} • Dias: ${item.days || '—'}`;
      case 'schools':
        return `${item.city_name || '—'} - ${item.state || '—'}`;
      case 'teachers':
        return item.social_name || item.cpf || '';
      case 'classes':
        return `Curso: ${item.course_name || '—'} • Unidade: ${item.unit_name || '—'}`;
      case 'enrollments':
        return item.class_education_name
          ? `Turma: ${item.class_education_name} • Curso: ${item.class_education_course?.name || '—'}`
          : (item.class_education || '—');
      default:
        return '';
    }
  };

  const renderRight = (item) => {
    switch (activeCard) {
      case 'subjects':
        return formatDate(item.date);
      case 'courses':
        return formatDate(item.start);
      case 'schools':
        return item.unit_code || '';
      case 'teachers':
        return formatDate(item.date_of_birth);
      case 'classes':
        return item.active ? 'Ativa' : 'Inativa';
      case 'enrollments':
        return `Início: ${formatDate(item.start)}`;
      default:
        return '';
    }
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container seduc-container">
        <Titulo children={<h2>Painel SEDUC</h2>} />

    

        <div className="seduc-cards">
          {cards.map((c) => (
            <button
              key={c.key}
              className={`seduc-card ${c.color} ${activeCard === c.key ? 'active' : ''}`}
              onClick={() => setActiveCard(c.key)}
            >
              <p className="label">{c.label}</p>
              <strong>{c.count}</strong>
            </button>
          ))}
        </div>

            <div className="seduc-header">
          <div className="seduc-search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="seduc-search-icon" />
            <input
              type="text"
              className="seduc-search"
              placeholder={placeholders[activeCard] || 'Buscar'}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          {loading && <span className="seduc-badge">Carregando...</span>}
        </div>

        <div className="seduc-section">
          <div className="seduc-section-header">
            <h4>{cards.find((c) => c.key === activeCard)?.label || 'Dados'}</h4>
            <span className="seduc-tag">{displayedData.length} registros</span>
          </div>
          <div className="seduc-list seduc-list--table">
            {displayedData.map((item) => {
              const title = item.name || item.type_name || item.internal_name || item.internal || 'Registro';
              const subtitle = renderSubtitle(item);
              const rightInfo = renderRight(item);
              return (
                <button
                  key={item.id}
                  className="seduc-row seduc-row--table"
                  onClick={() => handleOpenDrawer(item, cards.find((c) => c.key === activeCard)?.label || 'Detalhe')}
                >
                  <div>
                    <p className="title">{title}</p>
                    {subtitle && <p className="muted">{subtitle}</p>}
                  </div>
                  <div className="right">
                    <span className="muted">{rightInfo}</span>
                  </div>
                </button>
              );
            })}
            {displayedData.length === 0 && <p className="muted">Nenhum registro encontrado.</p>}
          </div>
          <div className="pagination-options">
            <div>
              <label htmlFor="itemsPerPage">Itens por página:</label>
              <select id="itemsPerPage" value={pageSize} onChange={handleItemsPerPageChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={Math.max(1, Math.ceil((countByCard[activeCard] || displayedData.length) / pageSize))}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              forcePage={(pageByCard[activeCard] || 1) - 1}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
            />
          </div>
        </div>
      </div>

      <Drawer
        open={drawer.open}
        title={drawer.title}
        rows={drawer.rows}
        onClose={handleCloseDrawer}
        wide
      />
    </div>
  );
};

export default PainelSeduc;
